import {
    BlockStack,
    Box,
    Button,
    Modal,
    SegmentedControl,
    TextField,
    Typography,
    type SegmentedOption,
} from '@pv-frontend/ui-components-web';

/** The `GrantTerm` enum members `grantPro` accepts. */
export type GrantTermValue = 'ONE_MONTH' | 'THREE_MONTHS' | 'TWELVE_MONTHS' | 'INDEFINITE';

const TERM_OPTIONS: readonly SegmentedOption<GrantTermValue>[] = [
    { value: 'ONE_MONTH', label: '1 month' },
    { value: 'THREE_MONTHS', label: '3 months' },
    { value: 'TWELVE_MONTHS', label: '12 months' },
    { value: 'INDEFINITE', label: 'Indefinite' },
];

/** The server's cap on a grant/revoke reason (the operator write audit's column width). */
export const REASON_MAX_LENGTH = 1000;

/**
 * Why a reason can't be sent yet, or null when it can. Mirrors the server's rule — non-blank, at most
 * 1000 characters — so the operator hears it before a round trip rather than as a refusal.
 */
export function reasonProblem(reason: string): 'blank' | 'too-long' | null {
    if (reason.trim().length === 0) return 'blank';
    if (reason.length > REASON_MAX_LENGTH) return 'too-long';
    return null;
}

export interface ProGrantModalProps {
    open: boolean;
    mode: 'grant' | 'revoke';
    onClose: () => void;
    vaultName: string;
    vaultIdentifier: string;
    /** The vault's plan as it stands right now — the guard against comping a misidentified vault. */
    currentPlan: string;
    /** A qualifier for the current plan (e.g. "until 1 Dec 2026"), when there is one. */
    currentPlanDetail: string | null;
    /** A grant on a live comp extends its window rather than starting a new one. */
    grantExtends: boolean;
    term: GrantTermValue;
    onTermChange: (term: GrantTermValue) => void;
    reason: string;
    onReasonChange: (reason: string) => void;
    onConfirm: () => void;
    submitting: boolean;
    /** Readable copy for a refused or failed commit; the modal stays open so the operator can see it. */
    error: string | null;
}

/**
 * The confirm step for granting or revoking Complimentary Pro (pv-frontend#607). Shows the vault and its
 * current plan before anything is committed, and will not commit without a reason — the reason is what
 * makes the record reviewable later. Presentational: the panel owns the state and the mutation.
 */
export function ProGrantModal({
    open,
    mode,
    onClose,
    vaultName,
    vaultIdentifier,
    currentPlan,
    currentPlanDetail,
    grantExtends,
    term,
    onTermChange,
    reason,
    onReasonChange,
    onConfirm,
    submitting,
    error,
}: ProGrantModalProps) {
    const problem = reasonProblem(reason);
    const isGrant = mode === 'grant';
    const confirmLabel = isGrant ? (grantExtends ? 'Extend grant' : 'Grant Pro') : 'Revoke';

    let title: string;
    let explainer: string;
    if (!isGrant) {
        title = 'Revoke Complimentary Pro';
        explainer = 'The grant ends now and the household falls back to Free immediately.';
    } else if (grantExtends) {
        title = 'Extend Complimentary Pro';
        explainer = 'This vault is already complimentary. Granting again extends its window by the term.';
    } else {
        title = 'Grant Complimentary Pro';
        explainer = 'The household gets Pro immediately, and returns to Free on its own when the term ends.';
    }

    return (
        <Modal
            open={open}
            onClose={onClose}
            title={title}
            testID="pro-grant-modal"
            actions={
                <>
                    <Button variant="tertiary" onPress={onClose} disabled={submitting}>
                        Cancel
                    </Button>
                    <Button
                        variant={isGrant ? 'primary' : 'critical'}
                        onPress={onConfirm}
                        isLoading={submitting}
                        disabled={problem !== null}
                        testID="pro-grant-confirm"
                    >
                        {confirmLabel}
                    </Button>
                </>
            }
        >
            <BlockStack gap="400">
                <Box
                    background="bg-card"
                    borderColor="border-card"
                    borderWidth="0165"
                    borderRadius="300"
                    padding="300"
                >
                    <BlockStack gap="100">
                        <Typography variant="body-md" fontWeight="semibold">
                            {vaultName}
                        </Typography>
                        <Typography variant="body-sm" tone="text-secondary">
                            {vaultIdentifier}
                        </Typography>
                        <Typography variant="body-sm" testID="pro-grant-current-plan">
                            {currentPlanDetail
                                ? `Current plan: ${currentPlan} · ${currentPlanDetail}`
                                : `Current plan: ${currentPlan}`}
                        </Typography>
                    </BlockStack>
                </Box>

                <Typography tone="text-secondary">{explainer}</Typography>

                {isGrant ? (
                    <BlockStack gap="150">
                        <Typography variant="body-sm" fontWeight="medium" tone="text-secondary">
                            Term
                        </Typography>
                        <SegmentedControl
                            accessibilityLabel="Grant term"
                            options={TERM_OPTIONS}
                            value={term}
                            onChange={onTermChange}
                            disabled={submitting}
                        />
                    </BlockStack>
                ) : null}

                <BlockStack gap="100">
                    <TextField
                        label="Reason"
                        value={reason}
                        onChange={onReasonChange}
                        placeholder={
                            isGrant ? 'e.g. Beta tester thank-you' : 'e.g. Granted to the wrong vault'
                        }
                        disabled={submitting}
                        error={
                            problem === 'too-long'
                                ? `Keep the reason to ${REASON_MAX_LENGTH} characters or fewer.`
                                : undefined
                        }
                        testID="pro-grant-reason"
                    />
                    <Typography variant="body-sm" tone="text-secondary">
                        Required. Recorded against your name for whoever reviews this later — never shown to
                        the household.
                    </Typography>
                </BlockStack>

                {error ? (
                    <Box
                        background="bg-card"
                        borderColor="border-card"
                        borderWidth="0165"
                        borderRadius="300"
                        padding="300"
                    >
                        <Typography tone="text-critical" testID="pro-grant-error">
                            {error}
                        </Typography>
                    </Box>
                ) : null}
            </BlockStack>
        </Modal>
    );
}
